/* eslint-disable react/no-array-index-key */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { XMLParser } from 'fast-xml-parser';
import { FaExchangeAlt, FaPlus } from 'react-icons/fa';

import Loading from '../../components/Loading';
import Modal from '../../components/Modal/Modal';

// ✅ já existe no teu projeto
import ProductNameSearch from '../../components/ProductNameSearch';

// ⚠️ AJUSTE OS CAMINHOS ABAIXO CONFORME TEU REPO
import ProductEdit from '../ProductEdit';
import MovementIn from '../MovementIn'; // <-- ajuste

import { Container } from '../../styles/layout/Container';

import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';

import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar } from '../../styles/layout/ActionBar';

import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

// =====================
// estilos locais
// =====================
const FileInputRow = styled.div`
   display: flex;
   gap: 12px;
   align-items: center;
   flex-wrap: wrap;

   input[type='file'] {
      max-width: 360px;
   }
`;

const Badge = styled.span`
   background-color: rgba(200, 200, 200, 1);
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 6px 12px;
   border-radius: 24px;
   font-size: 12px;
   font-weight: 800;
   letter-spacing: 0.2px;
   border: 1px solid rgba(0, 0, 0, 0.15);
`;

const Muted = styled.span`
   opacity: 0.75;
`;

const IconBtn = styled.button`
   border: 0;
   padding: 6px 8px;
   border-radius: 10px;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;

   &:hover {
      background: rgba(1, 151, 246, 0.15);
   }

   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }
`;

const ModalScroll = styled.div`
   max-height: 72vh;
   overflow: auto;
   padding-right: 6px;
   background-color: #373737;
`;

// =====================
// helpers
// =====================
function asArray(v) {
   if (!v) return [];
   return Array.isArray(v) ? v : [v];
}

function safeNumber(v) {
   const n = Number(String(v ?? '').replace(',', '.'));
   return Number.isFinite(n) ? n : 0;
}

function pickFirstRoot(obj) {
   if (!obj || typeof obj !== 'object') return null;
   const k = Object.keys(obj)[0];
   return k ? obj[k] : null;
}

/**
 * Extrai dados básicos de NFe:
 * - nNF, dhEmi, emit.xNome, dest.xNome
 * - itens det[].prod: cProd, cEAN, xProd, NCM, CFOP, uCom, qCom, vUnCom, vProd
 */
function parseNfeXml(xmlText) {
   const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: '@_',
      removeNSPrefix: true, // ajuda com XML com namespace
      parseTagValue: true,
      trimValues: true,
   });

   const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');

   function extractDocKey({ root, infNFe }) {
      // 1) nfeProc -> protNFe -> infProt -> chNFe
      const byProt = root?.protNFe?.infProt?.chNFe;
      const protKey = onlyDigits(byProt);
      if (protKey.length === 44) return protKey;

      // 2) infNFe attribute Id="NFe3126..."
      const rawId = infNFe?.['@_Id'] ?? infNFe?.['@_id'];
      const cleaned = onlyDigits(String(rawId ?? '').replace(/^NFe/i, ''));
      if (cleaned.length === 44) return cleaned;

      return '';
   }

   const json = parser.parse(xmlText);

   // alguns arquivos vêm como nfeProc -> NFe -> infNFe
   // outros podem vir direto como NFe
   const root = json?.nfeProc ?? json?.NFe ?? pickFirstRoot(json) ?? json;

   const nfe = root?.NFe ?? root;
   const infNFe = nfe?.infNFe ?? nfe?.NFe?.infNFe;

   if (!infNFe) {
      throw new Error(
         'Não encontrei infNFe no XML. Verifique se é um XML de NF-e válido.'
      );
   }

   const ide = infNFe?.ide ?? {};
   const emit = infNFe?.emit ?? {};
   const dest = infNFe?.dest ?? {};

   // ✅ FIX: docKey precisa ser calculado antes de usar no meta
   const docKey = extractDocKey({ root, infNFe });

   const meta = {
      nNF: String(ide?.nNF ?? ''),
      dhEmi: String(ide?.dhEmi ?? ''),
      natOp: String(ide?.natOp ?? ''),
      emitName: String(emit?.xNome ?? ''),
      emitCnpj: String(emit?.CNPJ ?? ''),
      destName: String(dest?.xNome ?? ''),
      destCnpj: String(dest?.CNPJ ?? ''),
      docKey,
   };

   const detList = asArray(infNFe?.det);

   const items = detList
      .map((det, idx) => {
         const prod = det?.prod ?? {};
         const nItem = det?.['@_nItem'] ?? String(idx + 1);

         const qty = safeNumber(prod?.qCom);
         const unitCost = safeNumber(prod?.vUnCom);

         return {
            rowId: `${nItem}-${idx}`,
            nItem: String(nItem),
            supplierSku: String(prod?.cProd ?? ''),
            ean: String(prod?.cEAN ?? ''),
            name: String(prod?.xProd ?? ''),
            ncm: String(prod?.NCM ?? ''),
            cfop: String(prod?.CFOP ?? ''),
            unit: String(prod?.uCom ?? ''),
            qty,
            unitCost,
            total: safeNumber(prod?.vProd),
            // associação
            searchValue: '',
            selectedProduct: null,
         };
      })
      .filter((x) => x.name || x.supplierSku);

   return { meta, items };
}

// =====================
// page
// =====================
export default function MovementInByXml() {
   const [isLoading, setIsLoading] = useState(false);

   const [xmlMeta, setXmlMeta] = useState(null);
   const [rows, setRows] = useState([]);

   // modais
   const [isNewProductOpen, setIsNewProductOpen] = useState(false);
   const [isMovementOpen, setIsMovementOpen] = useState(false);

   const handleReset = () => {
      setXmlMeta(null);
      setRows([]);
   };

   const handleFile = async (file) => {
      if (!file) return;

      try {
         setIsLoading(true);

         const text = await file.text();
         const parsed = parseNfeXml(text);

         setXmlMeta(parsed.meta);
         setRows(parsed.items);

         toast.success(
            `XML lido com sucesso. Itens encontrados: ${parsed.items.length}`
         );
      } catch (err) {
         console.error(err);
         toast.error(err?.message || 'Falha ao ler XML.');
         handleReset();
      } finally {
         setIsLoading(false);
      }
   };

   const handleChangeSearchValue = (rowIndex, value) => {
      setRows((prev) =>
         prev.map((r, idx) =>
            idx === rowIndex ? { ...r, searchValue: value } : r
         )
      );
   };

   const handleSelectProduct = (rowIndex, product) => {
      setRows((prev) =>
         prev.map((r, idx) =>
            idx === rowIndex ? { ...r, selectedProduct: product } : r
         )
      );
   };

   const handleSwapProduct = (rowIndex) => {
      setRows((prev) =>
         prev.map((r, idx) =>
            idx === rowIndex
               ? { ...r, selectedProduct: null, searchValue: '' }
               : r
         )
      );
   };

   const handleChangeUnitCost = (rowIndex, next) => {
      const nextNum = safeNumber(next);
      setRows((prev) =>
         prev.map((r, idx) =>
            idx === rowIndex
               ? {
                    ...r,
                    unitCost: nextNum,
                    total: nextNum * (r.qty || 0),
                 }
               : r
         )
      );
   };

   const associationReady = useMemo(() => {
      if (!rows.length) return false;
      return rows.every((r) => !!r.selectedProduct?.id);
   }, [rows]);

   const movementDraft = useMemo(() => {
      if (!xmlMeta) return null;

      // ✅ Agrupa itens com mesmo prod_id somando qtd
      const grouped = Object.values(
         rows
            .filter((r) => r.selectedProduct?.id)
            .reduce((acc, r) => {
               const key = r.selectedProduct.id;
               if (!acc[key]) {
                  acc[key] = {
                     prod_id: r.selectedProduct.id,
                     prod_qtd: r.qty,
                     quantity: r.qty,
                     sku: String(
                        r.selectedProduct.sku ??
                           r.selectedProduct.prod_sku ??
                           ''
                     ),
                     prod_name:
                        r.selectedProduct.name ?? r.selectedProduct.prod_name,
                     unit_cost: r.unitCost, // pega o custo do primeiro encontrado
                     xml_name: r.name,
                  };
               } else {
                  acc[key].prod_qtd += r.qty; // ✅ soma
                  acc[key].quantity += r.qty; // ✅ soma
               }
               return acc;
            }, {})
      );

      return {
         move_type: 'in',
         doc_type: 'NF-e (XML)',
         doc_number: xmlMeta.nNF || '',
         doc_key: xmlMeta.docKey || '',
         movement_description: `Entrada via XML NF-e #${xmlMeta.nNF || ''} - ${xmlMeta.emitName || ''}`,
         items: grouped,
      };
   }, [xmlMeta, rows]);

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Importar XML (Entrada)</PageTitle>
               <PageSubtitle>
                  Leia o XML, associe cada item a um produto e siga para criar o
                  movimento.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               {!!rows.length && (
                  <Badge>
                     Itens: {rows.length} •{' '}
                     <Muted>
                        {associationReady
                           ? 'Associações ok'
                           : 'Associações pendentes'}
                     </Muted>
                  </Badge>
               )}
            </PageHeaderActions>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Arquivo XML</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <FormGrid $cols={12} $colsMobile={12}>
                  <Field $col={12} $colMobile={12}>
                     <label>Anexar XML</label>

                     <FileInputRow>
                        <input
                           type="file"
                           accept=".xml,text/xml,application/xml"
                           onChange={(e) => handleFile(e.target.files?.[0])}
                        />

                        <button
                           type="button"
                           onClick={handleReset}
                           disabled={!rows.length}
                        >
                           Limpar
                        </button>

                        <button
                           type="button"
                           disabled={!associationReady}
                           onClick={() => setIsMovementOpen(true)}
                           title={
                              associationReady
                                 ? 'Abrir criação do movimento'
                                 : 'Associe todos os itens antes'
                           }
                        >
                           Criar movimento
                        </button>
                     </FileInputRow>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {!!xmlMeta && (
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados da NF-e</SectionTitle>
               </SectionHeader>

               <SectionBody>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={3} $colMobile={12}>
                        <label>Nº NF</label>
                        <input value={xmlMeta.nNF || ''} disabled />
                     </Field>

                     <Field $col={5} $colMobile={12}>
                        <label>Emitente</label>
                        <input
                           value={`${xmlMeta.emitName || ''}${
                              xmlMeta.emitCnpj ? ` (${xmlMeta.emitCnpj})` : ''
                           }`}
                           disabled
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Destinatário</label>
                        <input
                           value={`${xmlMeta.destName || ''}${
                              xmlMeta.destCnpj ? ` (${xmlMeta.destCnpj})` : ''
                           }`}
                           disabled
                        />
                     </Field>

                     {/* ✅ FIX: exibir chave em tela */}
                     <Field $col={12} $colMobile={12}>
                        <label>Chave de acesso</label>
                        <input value={xmlMeta.docKey || ''} disabled />
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>
         )}

         <Section>
            <SectionHeader>
               <SectionTitle>Itens do XML</SectionTitle>
            </SectionHeader>

            <SectionBody>
               {!rows.length ? (
                  <EmptyState
                     title="Nenhum item carregado"
                     description="Anexe um XML de NF-e para listar os itens aqui."
                  />
               ) : (
                  <>
                     <ActionBar>
                        <div
                           style={{
                              display: 'flex',
                              gap: 10,
                              flexWrap: 'wrap',
                           }}
                        >
                           <Muted>
                              Dica: associe usando nome ou SKU. Ao selecionar, o
                              campo trava e aparece o botão de troca.
                           </Muted>
                        </div>
                     </ActionBar>

                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th style={{ width: 70 }}>Item</th>
                                 <th>Produto (XML)</th>
                                 <th style={{ width: 110 }}>Qtd</th>
                                 <th style={{ width: 160 }}>Custo unit.</th>
                                 <th>Associar produto</th>
                                 <th style={{ width: 70 }}>Ações</th>
                              </tr>
                           </thead>

                           <tbody>
                              {rows.map((r, idx) => {
                                 const selected = r.selectedProduct;
                                 const isSelected = !!selected?.id;

                                 return (
                                    <tr key={r.rowId}>
                                       <td>{r.nItem}</td>

                                       <td>
                                          <div style={{ fontWeight: 800 }}>
                                             {r.name || '--'}
                                          </div>

                                          <div
                                             style={{
                                                fontSize: 12,
                                                opacity: 0.75,
                                             }}
                                          >
                                             <span>
                                                <strong>cProd:</strong>{' '}
                                                {r.supplierSku || '--'}
                                             </span>
                                             {' • '}
                                             <span>
                                                <strong>NCM:</strong>{' '}
                                                {r.ncm || '--'}
                                             </span>
                                             {' • '}
                                             <span>
                                                <strong>EAN:</strong>{' '}
                                                {r.ean || '--'}
                                             </span>
                                          </div>
                                       </td>

                                       <td style={{ textAlign: 'right' }}>
                                          {Number.isFinite(r.qty)
                                             ? r.qty
                                             : '--'}{' '}
                                          {r.unit}
                                       </td>

                                       <td>
                                          <input
                                             type="number"
                                             step="0.01"
                                             value={r.unitCost}
                                             onChange={(e) =>
                                                handleChangeUnitCost(
                                                   idx,
                                                   e.target.value
                                                )
                                             }
                                          />
                                       </td>

                                       <td>
                                          <ProductNameSearch
                                             value={r.searchValue}
                                             onChange={(v) =>
                                                handleChangeSearchValue(idx, v)
                                             }
                                             onSelect={(p) =>
                                                handleSelectProduct(idx, p)
                                             }
                                             disabled={isSelected}
                                             placeholder="Buscar por SKU ou nome..."
                                             displayMode="sku-name"
                                             nameLike
                                          />

                                          {isSelected && (
                                             <div
                                                style={{
                                                   marginTop: 6,
                                                   fontSize: 12,
                                                   opacity: 0.75,
                                                }}
                                             >
                                                Associado:{' '}
                                                <strong>
                                                   {selected.sku ??
                                                      selected.prod_sku}{' '}
                                                   -{' '}
                                                   {selected.name ??
                                                      selected.prod_name}
                                                </strong>
                                             </div>
                                          )}
                                       </td>

                                       <td
                                          style={{
                                             textAlign: 'center',
                                             display: 'flex',
                                             justifyContent: 'center',
                                             alignItems: 'center',
                                          }}
                                       >
                                          <IconBtn
                                             type="button"
                                             onClick={() =>
                                                handleSwapProduct(idx)
                                             }
                                             disabled={!isSelected}
                                             title="Trocar produto"
                                          >
                                             <FaExchangeAlt />
                                          </IconBtn>
                                          <IconBtn
                                             type="button"
                                             onClick={() =>
                                                setIsNewProductOpen(true)
                                             }
                                             title="Cadastrar novo produto"
                                          >
                                             <FaPlus />
                                          </IconBtn>
                                       </td>
                                    </tr>
                                 );
                              })}
                           </tbody>
                        </Table>
                     </TableWrapper>
                  </>
               )}
            </SectionBody>
         </Section>

         {/* Modal: Novo produto */}
         <Modal
            isOpen={isNewProductOpen}
            title="Cadastrar novo produto"
            onClose={() => setIsNewProductOpen(false)}
            footer={
               <button type="button" onClick={() => setIsNewProductOpen(false)}>
                  Fechar
               </button>
            }
         >
            <ModalScroll>
               {/* Renderiza o teu componente real */}
               <ProductEdit />
            </ModalScroll>
         </Modal>

         {/* Modal: Criar movimento */}
         <Modal
            isOpen={isMovementOpen}
            title="Criar movimento (entrada)"
            onClose={() => setIsMovementOpen(false)}
            footer={
               <>
                  <button
                     type="button"
                     onClick={() => setIsMovementOpen(false)}
                  >
                     Fechar
                  </button>
               </>
            }
         >
            <ModalScroll>
               {/* Prévia do payload gerado (útil pro teu endpoint depois) */}
               {/*

               <Section>
                  <SectionHeader>
                     <SectionTitle>
                        Prévia do payload (XML → Movimento)
                     </SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <pre
                        style={{
                           margin: 0,
                           padding: 12,
                           borderRadius: 10,
                           background: 'rgba(0,0,0,0.05)',
                           overflow: 'auto',
                        }}
                     >
                        {JSON.stringify(movementDraft, null, 2)}
                     </pre>
                  </SectionBody>
               </Section>
               */}

               {/* Teu componente real (página) dentro do modal.
                   Para “entrar com os dados”, o próximo passo é refatorar o MovementIn
                   para aceitar um prop (ex.: prefill) e inicializar states. */}
               <Section>
                  <SectionHeader>
                     <SectionTitle>
                        Entrada de movimento via importação XML
                     </SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <MovementIn prefill={movementDraft} />
                  </SectionBody>
               </Section>
            </ModalScroll>
         </Modal>
      </Container>
   );
}
